import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Paper,
  Divider,
  Chip
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  CheckCircle as CheckIcon,
  Print as PrintIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { formatDateTime } from '../utils/dateUtils';

const PaymentReceiptDialog = ({ open, onClose, paymentData, classData }) => {
  const handlePrintReceipt = () => {
    window.print();
  };

  const handleDownloadReceipt = () => {
    // Create receipt content as text
    const receiptContent = `
CLASS BOOKING RECEIPT
=====================

Class Details:
- Class: ${classData?.className || 'N/A'}
- Date: ${classData?.startTime ? formatDateTime(classData.startTime) : 'N/A'}
- Trainer: ${classData?.trainer ? `${classData.trainer.firstName} ${classData.trainer.lastName}` : 'N/A'}
- Facility: ${classData?.facility?.name || 'N/A'}

Payment Details:
- Amount: ${paymentData?.amount || 0} ${paymentData?.currency || 'TRY'}
- Method: ${paymentData?.paymentMethod || 'N/A'}
- Token: ${paymentData?.paymentToken || 'N/A'}
- Status: ${paymentData?.status || 'N/A'}

Thank you for your booking!
`;

    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt_${paymentData?.paymentToken || 'booking'}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  if (!paymentData && !classData) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { 
          minHeight: 500,
          '@media print': {
            boxShadow: 'none',
            margin: 0,
            maxWidth: 'none',
            width: '100%'
          }
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
        <Typography variant="h5" gutterBottom>
          Payment Successful
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your class booking has been confirmed
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            border: '1px solid', 
            borderColor: 'divider',
            '@media print': {
              boxShadow: 'none',
              border: 'none'
            }
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <ReceiptIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              BOOKING RECEIPT
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Receipt #: {paymentData?.paymentToken || classData?._id || 'N/A'}
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Class Details */}
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Class Details
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Class:
              </Typography>
              <Typography variant="body2">
                {classData?.className || 'N/A'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Date & Time:
              </Typography>
              <Typography variant="body2">
                {classData?.startTime ? formatDateTime(classData.startTime) : 'N/A'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Trainer:
              </Typography>
              <Typography variant="body2">
                {classData?.trainer ? `${classData.trainer.firstName} ${classData.trainer.lastName}` : 'N/A'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Location:
              </Typography>
              <Typography variant="body2">
                {classData?.facility?.name || 'N/A'}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Payment Details */}
          {paymentData && (
            <>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Payment Details
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Amount:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {paymentData.amount} {paymentData.currency}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Payment Method:
                  </Typography>
                  <Typography variant="body2">
                    {paymentData.paymentMethod === 'credit_card' && 'Credit Card'}
                    {paymentData.paymentMethod === 'wire_transfer' && 'Wire Transfer'}
                    {paymentData.paymentMethod === 'in_place' && 'Pay at Gym'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Transaction ID:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {paymentData.paymentToken}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Status:
                  </Typography>
                  <Chip 
                    label={paymentData.status === 'completed' ? 'PAID' : paymentData.status.toUpperCase()}
                    color={paymentData.status === 'completed' ? 'success' : 'default'}
                    size="small"
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Date:
                  </Typography>
                  <Typography variant="body2">
                    {formatDateTime(new Date())}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />
            </>
          )}

          {/* Footer */}
          <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2" gutterBottom>
              Thank you for booking with us!
            </Typography>
            <Typography variant="caption">
              Please arrive 10 minutes before class starts.
            </Typography>
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, '@media print': { display: 'none' } }}>
        <Button onClick={handlePrintReceipt} startIcon={<PrintIcon />}>
          Print
        </Button>
        <Button onClick={handleDownloadReceipt} startIcon={<DownloadIcon />}>
          Download
        </Button>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentReceiptDialog;